import React, { useMemo } from 'react';

import Tooltip from 'common/components/Tooltip';
import Quantity from 'common/components/Quantity';
import ErrorMessage from 'common/components/ErrorMessage';

import {
  FieldPath, Control, Controller, RegisterOptions,
} from 'react-hook-form';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { FallbackFormValues } from '../../../../../utils/type-tools';
import { GeneratorChangeHandlerType } from '../../../../../common/components/FormHF/types';
import { GeneratorFieldType } from '../../../../../common/components/FieldGenerator/types';
import { ValidationTypes } from '../../../../../common/types/fields';
import { isMaxValidationType, isMinValidationType } from '../../../../../utils/isMinMaxValidation';
import { getValidationErrorMessage } from '../../../../../utils/getValidationErrorMessage';

export const MIN_QUANTITY = 1;
export const MAX_QUANTITY = 999;

interface QuantityProps<TFormValue extends FallbackFormValues> {
    name: FieldPath<TFormValue>,
    control: Control<TFormValue>,
    onChangeHandler?: GeneratorChangeHandlerType<TFormValue>,
    field: GeneratorFieldType,
    className?: string,
}

export const QuantityConnected = <TFormValue extends FallbackFormValues>({
  className,
  name,
  control,
  onChangeHandler,
  field,
}: QuantityProps<TFormValue>) => {
  const rulesObject = useMemo<RegisterOptions>(() => {
    const rulesTemplate: RegisterOptions = {};
    field.validation?.forEach((v) => {
      if (v.type === ValidationTypes.required) {
        rulesTemplate.required = {
          value: true,
          message: getValidationErrorMessage(v),
        };
      } else if (v.type === ValidationTypes.range) {
        if (isMaxValidationType(v)) {
          if (
            !rulesTemplate.max
              || (typeof rulesTemplate.max === 'object' && rulesTemplate.max.value > v.maxValue)
              || (typeof rulesTemplate.max !== 'object' && rulesTemplate.max > v.maxValue)
          ) {
            rulesTemplate.max = {
              value: v.maxValue,
              message: getValidationErrorMessage(v),
            };
          }
        }
        if (isMinValidationType(v)) {
          if (
            !rulesTemplate.min
              || (typeof rulesTemplate.min === 'object' && rulesTemplate.min.value < v.minValue)
              || (typeof rulesTemplate.min !== 'object' && rulesTemplate.min < v.minValue)
          ) {
            rulesTemplate.min = {
              value: v.minValue,
              message: getValidationErrorMessage(v),
            };
          }
        }
      }
    });
    return rulesTemplate;
  }, [field]);

  return (
    <Controller
      name={name}
      control={control}
      rules={rulesObject}
      render={({
        field: controllerField,
        fieldState: { error },
      }) => {
        const configQuantity = (controllerField.value as number) || MIN_QUANTITY;

        const handleSetCounter = (count: number) => {
          const validatedCount = Math.max(Math.min(count, MAX_QUANTITY), MIN_QUANTITY);
          controllerField.onChange(validatedCount);
          if (onChangeHandler) {
            onChangeHandler(field.type, name, validatedCount as TFormValue[keyof TFormValue]);
          }
        };

        return (
          <>
            <div className={classNames(styles.quantityLabel, 'common-components-Input__label')}>
              Assembly Qty:
              <Tooltip
                className={styles.iconTooltip}
                content="Please enter the required quantity for this assembly."
              />
            </div>
            <Quantity
              className={className}
              count={configQuantity}
              handleSetCounter={handleSetCounter}
            />
            {error && error.message && <ErrorMessage text={error.message} />}
          </>
        );
      }}
    />
  );
};
